<template>
  <div class="ad-detail-page">
    <div class="detail-toolbar mb-1">
      <b-button variant="outline-secondary" size="sm" @click="goBack">
        <feather-icon icon="ArrowLeftIcon" size="14" class="mr-50" />
        Back
      </b-button>
      <div v-if="ad" class="detail-toolbar__actions">
        <b-button variant="outline-primary" size="sm" class="mr-50" @click="openEditModal">
          <feather-icon icon="EditIcon" size="14" class="mr-50" />
          Edit Ad
        </b-button>
        <b-button
          v-if="showModeration"
          variant="success"
          size="sm"
          class="mr-50"
          :disabled="actionInFlight"
          @click="approveAd"
        >
          Approve
        </b-button>
        <b-button
          v-if="showModeration"
          variant="warning"
          size="sm"
          class="mr-50"
          :disabled="actionInFlight"
          @click="rejectAd"
        >
          Reject
        </b-button>
        <b-button
          variant="outline-danger"
          size="sm"
          :disabled="actionInFlight"
          @click="deleteAd"
        >
          Delete
        </b-button>
      </div>
    </div>

    <div v-if="isLoading" class="text-center py-3">
      <b-spinner label="Loading ad details..." />
    </div>
    <b-alert v-else-if="loadError" show variant="danger" class="mb-2">
      {{ loadError }}
      <b-button size="sm" variant="outline-danger" class="ml-1" @click="fetchAd">Retry</b-button>
    </b-alert>

    <template v-else-if="ad">
      <b-row>
        <b-col lg="7" md="12" class="mb-2">
          <b-card no-body class="media-card">
            <div v-if="primaryVideo" class="media-wrap">
              <video :src="primaryVideo.url" controls class="ad-video" />
            </div>
            <div v-else-if="primaryImage" class="media-wrap">
              <b-img :src="primaryImage" fluid class="ad-image" alt="Ad media" />
            </div>
            <div v-else class="text-muted text-center py-3">
              No media available
            </div>
            <div v-if="extraMedia.length" class="media-thumbs p-1">
              <button
                v-for="(item, idx) in extraMedia"
                :key="`thumb-${idx}`"
                type="button"
                class="thumb-btn"
                @click="selectedMediaIndex = mediaItems.indexOf(item)"
              >
                <b-img v-if="item.type === 'image'" :src="item.url" class="thumb-img" />
                <span v-else class="thumb-video">Video</span>
              </button>
            </div>
          </b-card>
        </b-col>

        <b-col lg="5" md="12">
          <b-card class="detail-card">
            <div class="d-flex align-items-center mb-1">
              <b-avatar :src="ad.company.logo" size="42" class="mr-1" />
              <div>
                <h4 class="mb-0">{{ ad.company.name || 'Company' }}</h4>
                <small class="text-muted">Ad #{{ ad.id }}</small>
              </div>
              <b-badge class="ml-auto text-capitalize" :variant="statusVariant">{{ ad.status }}</b-badge>
            </div>

            <h3 class="ad-title">{{ ad.title || 'Untitled ad' }}</h3>
            <p class="ad-description">{{ ad.description || 'No description provided.' }}</p>

            <dl class="detail-list">
              <dt>Advertiser / Company</dt>
              <dd>{{ ad.company.name || '—' }} <span v-if="ad.company.id" class="text-muted">(ID {{ ad.company.id }})</span></dd>
              <dt>Brand</dt>
              <dd>
                <div class="d-flex align-items-center">
                  <b-avatar v-if="ad.brand && ad.brand.logo" :src="ad.brand.logo" size="28" class="mr-50" />
                  <span>{{ ad.brand && ad.brand.name ? ad.brand.name : '—' }}</span>
                </div>
                <small v-if="ad.brand && ad.brand.website" class="d-block">
                  <b-link :href="ad.brand.website" target="_blank" rel="noopener">{{ ad.brand.website }}</b-link>
                </small>
                <small v-if="ad.brand && ad.brand.description" class="d-block text-muted">{{ ad.brand.description }}</small>
              </dd>
              <dt>Primary Category</dt>
              <dd>{{ ad.category || '—' }}</dd>
              <dt>Secondary Category</dt>
              <dd>{{ ad.secondary_category || '—' }}</dd>
              <dt>Age Restriction</dt>
              <dd>{{ ad.min_age > 0 ? `${ad.min_age}+` : 'None' }}</dd>
              <dt>Active</dt>
              <dd>{{ ad.is_active ? 'Yes' : 'No' }}</dd>
              <dt>CTA Label</dt>
              <dd>{{ ad.cta_label || '—' }}</dd>
              <dt>CTA Link</dt>
              <dd>
                <b-link v-if="ad.cta_link" :href="ad.cta_link" target="_blank" rel="noopener">
                  {{ ad.cta_link }}
                </b-link>
                <span v-else>—</span>
              </dd>
              <dt>Slug</dt>
              <dd>{{ ad.slug || '—' }}</dd>
              <dt>Created</dt>
              <dd>{{ ad.created_at || '—' }}</dd>
              <dt>Publish date / time</dt>
              <dd>{{ publishLabel }}</dd>
              <dt>Expiry date / time</dt>
              <dd>{{ expiryLabel }}</dd>
            </dl>

            <div class="stats-row">
              <span>{{ ad.stats.views }} Views</span>
              <span>{{ ad.stats.clicks }} Clicks</span>
              <span>{{ ad.stats.likes }} Likes</span>
              <span>{{ ad.stats.dislikes }} Dislikes</span>
              <span>{{ ad.stats.shares }} Shares</span>
              <span>{{ ad.stats.reports }} Reports</span>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </template>

    <!-- Full edit modal -->
    <b-modal
      v-model="editModalOpen"
      centered
      size="lg"
      hide-footer
      no-close-on-backdrop
      title="Edit Ad"
      modal-class="ad-edit-modal"
      @hidden="resetEditForm"
    >
      <validation-observer ref="editFormObserver">
        <b-row>
          <b-col md="6">
            <mypoda-form-field label="Title" name="Title" rules="required|max:255" required-mark>
              <template #default="{ state, inputId }">
                <b-form-input :id="inputId" v-model="editForm.title" :state="state" />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col md="6">
            <mypoda-form-field label="Advertiser" name="Advertiser" rules="required" required-mark>
              <template #default="{ state, inputId }">
                <b-form-select :id="inputId" v-model="editForm.company" :options="companyOptions" :state="state" @change="onEditAdvertiserChange" />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col md="6">
            <mypoda-form-field label="Brand" name="Brand" rules="required" required-mark>
              <template #default="{ state, inputId }">
                <b-form-select :id="inputId" v-model="editForm.brandId" :options="brandOptions" :state="state" :disabled="!editForm.company" />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col cols="12">
            <mypoda-form-field label="Description" name="Description" rules="required|max:5000" required-mark>
              <template #default="{ state, inputId }">
                <b-form-textarea :id="inputId" v-model="editForm.description" :state="state" rows="3" />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col md="6">
            <mypoda-form-field label="Primary Category" name="Primary Category" rules="required" required-mark>
              <template #default="{ state, inputId }">
                <b-form-select
                  :id="inputId"
                  v-model="editForm.categoryId"
                  :options="primaryCategoryOptions"
                  :state="state"
                  @change="onEditPrimaryCategoryChange"
                />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col md="6">
            <mypoda-form-field label="Secondary Category" name="Secondary Category">
              <template #default="{ state, inputId }">
                <b-form-select
                  :id="inputId"
                  v-model="editForm.secondaryCategoryId"
                  :options="secondaryCategoryOptions"
                  :state="state"
                  :disabled="!editForm.categoryId"
                >
                  <template #first>
                    <b-form-select-option :value="null">None (optional)</b-form-select-option>
                  </template>
                </b-form-select>
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col md="6">
            <mypoda-form-field label="Age Restriction" name="Age Restriction">
              <template #default="{ state, inputId }">
                <b-form-select :id="inputId" v-model="editForm.minAge" :options="ageOptions" :state="state" />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col md="6">
            <mypoda-form-field label="CTA Label" name="CTA Label" rules="required|max:255" required-mark>
              <template #default="{ state, inputId }">
                <b-form-input :id="inputId" v-model="editForm.ctaLabel" :state="state" />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col cols="12">
            <mypoda-form-field label="CTA Link / Destination URL" name="CTA Link" rules="required|url" required-mark>
              <template #default="{ state, inputId }">
                <b-form-input :id="inputId" v-model="editForm.ctaLink" :state="state" placeholder="https://" />
              </template>
            </mypoda-form-field>
          </b-col>

          <b-col cols="12">
            <h5 class="edit-section-title">Publish &amp; Expiry</h5>
          </b-col>
          <b-col md="6">
            <mypoda-form-field label="Publish Date" name="Publish Date" rules="required" required-mark>
              <template #default="{ state, inputId }">
                <b-form-input :id="inputId" v-model="editForm.startDate" type="date" :state="state" />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col md="6">
            <mypoda-form-field label="Publish Time" name="Publish Time" rules="required" required-mark>
              <template #default="{ state, inputId }">
                <b-form-input :id="inputId" v-model="editForm.startTime" type="time" :state="state" />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col md="6">
            <mypoda-form-field label="Expiry Date" name="Expiry Date" rules="required" required-mark>
              <template #default="{ state, inputId }">
                <b-form-input :id="inputId" v-model="editForm.endDate" type="date" :state="state" />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col md="6">
            <mypoda-form-field label="Expiry Time" name="Expiry Time" rules="required" required-mark>
              <template #default="{ state, inputId }">
                <b-form-input :id="inputId" v-model="editForm.endTime" type="time" :state="state" />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col v-if="scheduleError" cols="12">
            <p class="text-danger mb-1">{{ scheduleError }}</p>
          </b-col>

          <b-col cols="12">
            <h5 class="edit-section-title">Media</h5>
            <div class="edit-media-row">
              <video
                v-if="editPreviewIsVideo"
                :src="editPreview"
                class="edit-preview"
                muted
                playsinline
                controls
              />
              <b-img v-else-if="editPreview" :src="editPreview" class="edit-preview" />
              <div>
                <input ref="editFileInput" type="file" class="d-none" :accept="editMediaAccept" @change="onEditFilePick">
                <b-button size="sm" variant="outline-primary" @click="$refs.editFileInput.click()">
                  {{ editPreview ? 'Change media' : 'Upload media' }}
                </b-button>
                <small class="d-block text-muted mt-50">
                  Images: JPG, PNG, GIF, WebP, SVG, BMP, AVIF, HEIC, TIFF.
                  Videos: MP4, MOV, WebM, M4V, AVI, MKV, MPEG, 3GP, OGV, WMV.
                  Max 20MB.
                </small>
              </div>
            </div>
          </b-col>
        </b-row>
      </validation-observer>

      <div class="d-flex justify-content-end mt-2">
        <b-button variant="outline-secondary" class="mr-1" :disabled="editSaving" @click="editModalOpen = false">
          Cancel
        </b-button>
        <b-button variant="primary" :disabled="editSaving" @click="saveEdit">
          <b-spinner v-if="editSaving" small class="mr-50" />
          Save Changes
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BFormTextarea,
  BImg,
  BLink,
  BModal,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import MypodaFormField from '@/components/mypoda/MypodaFormField.vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import {
  extractApiErrorMessage,
  isAdPendingStatus,
  resolveMediaUrl,
  unwrapAdminResponse,
} from '@/utils/adminApiHelpers'
import {
  confirmAction,
  notifyError,
  notifySuccess,
  promptRejectReason,
} from '@/utils/uiActions'
import {
  AD_IMAGE_FORMAT_ERROR,
  AD_VIDEO_FORMAT_ERROR,
  adMediaAccept,
  isAdVideoFile,
  isAllowedAdImageFile,
  isAllowedAdVideoFile,
  isVideoMediaUrl,
} from '@/utils/adMedia'

export default {
  name: 'AdDetail',
  components: {
    BAlert,
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BFormTextarea,
    BImg,
    BLink,
    BModal,
    BRow,
    BSpinner,
    MypodaFormField,
    ValidationObserver,
  },
  data() {
    return {
      isLoading: false,
      loadError: '',
      actionInFlight: false,
      ad: null,
      selectedMediaIndex: 0,
      editModalOpen: false,
      editSaving: false,
      scheduleError: '',
      categories: [],
      companies: [],
      brands: [],
      selectedEditFile: null,
      editFilePreviewUrl: '',
      existingImage: '',
      editForm: {
        title: '',
        description: '',
        company: '',
        brandId: '',
        categoryId: null,
        secondaryCategoryId: null,
        minAge: 0,
        ctaLabel: '',
        ctaLink: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
      },
      ageOptions: [
        { value: 0, text: 'No age restriction' },
        { value: 13, text: '13 years and older' },
        { value: 16, text: '16 years and older' },
        { value: 18, text: '18 years and older' },
        { value: 21, text: '21 years and older' },
      ],
    }
  },
  computed: {
    adId() {
      return this.$route.params.id
    },
    mediaItems() {
      const list = (this.ad?.media || []).map(m => ({
        type: m.type || 'image',
        url: resolveMediaUrl(m.url || m.thumbnail),
      }))
      if (!list.length && this.ad?.image) {
        list.push({ type: 'image', url: resolveMediaUrl(this.ad.image) })
      }
      return list
    },
    activeMedia() {
      return this.mediaItems[this.selectedMediaIndex] || this.mediaItems[0] || null
    },
    primaryImage() {
      return this.activeMedia?.type === 'image' ? this.activeMedia.url : null
    },
    primaryVideo() {
      return this.activeMedia?.type === 'video' ? this.activeMedia : null
    },
    extraMedia() {
      return this.mediaItems.filter((_, i) => i !== this.selectedMediaIndex).slice(0, 4)
    },
    publishLabel() {
      if (!this.ad) return '—'
      const date = this.ad.publish_at || this.ad.scheduled_at?.date || ''
      const time = this.ad.publish_time || this.ad.scheduled_at?.time || ''
      return [date, time].filter(Boolean).join(' · ') || '—'
    },
    expiryLabel() {
      if (!this.ad) return '—'
      const date = this.ad.expires_at || ''
      const time = this.ad.expires_time || ''
      return [date, time].filter(Boolean).join(' · ') || '—'
    },
    statusVariant() {
      const s = (this.ad?.status || '').toLowerCase()
      if (s.includes('approv') || s === 'active' || s === 'live') return 'light-success'
      if (s.includes('reject') || s === 'expired' || s === 'inactive') return 'light-danger'
      return 'light-warning'
    },
    showModeration() {
      return isAdPendingStatus(this.ad?.status) || String(this.ad?.status || '').toLowerCase() === 'draft'
    },
    primaryCategoryOptions() {
      return this.categories
        .filter(c => !c.parentId)
        .map(c => ({ value: c.id, text: c.name }))
    },
    secondaryCategoryOptions() {
      const primaryId = Number(this.editForm.categoryId || 0)
      if (!primaryId) return []
      return this.categories
        .filter(c => Number(c.parentId) === primaryId)
        .map(c => ({ value: c.id, text: c.name }))
    },
    brandOptions() {
      if (!this.editForm.company) {
        return [{ value: '', text: 'Select an advertiser first', disabled: true }]
      }
      if (!this.brands.length) {
        return [{ value: '', text: 'No brands for this advertiser', disabled: true }]
      }
      return [
        { value: '', text: 'Select brand', disabled: true },
        ...this.brands.map(brand => ({ value: String(brand.id), text: brand.name })),
      ]
    },
    companyOptions() {
      return [
        { value: '', text: 'Select advertiser', disabled: true },
        ...this.companies.map(c => ({ value: String(c.id), text: c.name })),
      ]
    },
    editPreview() {
      return this.editFilePreviewUrl || this.existingImage || ''
    },
    editPreviewIsVideo() {
      if (this.selectedEditFile) return isAdVideoFile(this.selectedEditFile)
      return isVideoMediaUrl(this.existingImage)
    },
    editMediaAccept() {
      return `${adMediaAccept('image')},${adMediaAccept('video')}`
    },
  },
  watch: {
    adId() {
      this.fetchAd()
    },
  },
  mounted() {
    this.fetchAd()
    this.loadCategories()
    this.loadCompanies()
  },
  beforeDestroy() {
    this.revokeEditPreview()
  },
  methods: {
    mapAd(raw) {
      return {
        id: raw.id,
        title: raw.title,
        slug: raw.slug || '',
        description: raw.description,
        image: resolveMediaUrl(raw.image),
        media: raw.media || [],
        cta_label: raw.cta_label || raw.cta || '',
        cta_link: raw.cta_link || '',
        category: raw.category || raw.category_name || '',
        category_id: raw.category_id || null,
        secondary_category: raw.secondary_category || raw.secondary_category_name || '',
        secondary_category_id: raw.secondary_category_id || null,
        min_age: Number(raw.min_age || 0),
        is_active: Boolean(raw.is_active),
        status: raw.status || '',
        created_at: raw.created_at || '',
        publish_at: raw.publish_at || '',
        publish_time: raw.publish_time || '',
        publish_at_raw: raw.publish_at_raw || '',
        expires_at: raw.expires_at || '',
        expires_time: raw.expires_time || '',
        expires_at_raw: raw.expires_at_raw || '',
        scheduled_at: raw.scheduled_at || {},
        company: {
          id: raw.company?.id || raw.advertiser_id || null,
          name: raw.company?.name || '',
          logo: resolveMediaUrl(raw.company?.logo),
        },
        stats: {
          likes: raw.stats?.likes ?? 0,
          dislikes: raw.stats?.dislikes ?? 0,
          views: raw.stats?.views ?? 0,
          shares: raw.stats?.shares ?? 0,
          reports: raw.stats?.reports ?? 0,
          clicks: raw.stats?.clicks ?? 0,
        },
      }
    },
    parseScheduleParts(rawDateTime, fallbackDate = '', fallbackTimeDisplay = '') {
      const value = String(rawDateTime || '').trim()
      const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?/)
      if (iso) {
        return { date: `${iso[1]}-${iso[2]}-${iso[3]}`, time: `${iso[4]}:${iso[5]}` }
      }
      const mdy = value.match(/^(\d{2})-(\d{2})-(\d{4})$/)
      let date = ''
      if (mdy) {
        date = `${mdy[3]}-${mdy[1]}-${mdy[2]}`
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        date = value
      } else if (fallbackDate) {
        date = this.parseScheduleParts(fallbackDate).date
      }

      let time = ''
      const timeStr = String(fallbackTimeDisplay || '').trim()
      const ampm = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
      if (ampm) {
        let hours = Number(ampm[1])
        const minutes = ampm[2]
        const period = ampm[3].toUpperCase()
        if (period === 'PM' && hours < 12) hours += 12
        if (period === 'AM' && hours === 12) hours = 0
        time = `${String(hours).padStart(2, '0')}:${minutes}`
      } else if (/^\d{2}:\d{2}/.test(timeStr)) {
        time = timeStr.slice(0, 5)
      }
      return { date, time }
    },
    async fetchAd() {
      if (!this.adId) return
      this.isLoading = true
      this.loadError = ''
      try {
        const res = await adminPanelApi.adPreview(this.adId)
        this.ad = this.mapAd(unwrapAdminResponse(res))
        this.selectedMediaIndex = 0
      } catch (e) {
        this.loadError = e.response?.data?.message || 'Unable to load ad details.'
        this.ad = null
      } finally {
        this.isLoading = false
      }
    },
    async loadCategories() {
      try {
        const res = await adminPanelApi.categories()
        const payload = unwrapAdminResponse(res)
        const list = payload?.categories || []
        this.categories = (Array.isArray(list) ? list : [])
          .filter(item => item.is_active !== false)
          .map(item => {
            const parentId = item.parent_id != null ? Number(item.parent_id) : null
            return {
              id: item.id,
              name: item.name,
              parentId: Number.isFinite(parentId) && parentId > 0 ? parentId : null,
            }
          })
      } catch (e) {
        this.categories = []
      }
    },
    async loadCompanies() {
      try {
        const res = await adminPanelApi.advertisers({ page: 1, per_page: 100 })
        const payload = unwrapAdminResponse(res)
        const list = payload.advertisers || []
        this.companies = list.map(item => ({
          id: item.id || item.company_id,
          name: item.company_name || item.companyName || `Company #${item.id || item.company_id}`,
        }))
      } catch (e) {
        this.companies = []
      }
    },
    async onEditAdvertiserChange(companyId) {
      const fromEvent = companyId != null && typeof companyId !== 'object' ? companyId : null
      this.editForm.brandId = ''
      await this.$nextTick()
      await this.loadBrands(fromEvent ?? this.editForm.company)
    },
    async loadBrands(companyId) {
      const id = Number(companyId || this.editForm.company || 0)
      if (!id) {
        this.brands = []
        return
      }
      try {
        const res = await adminPanelApi.brands({ company_id: id })
        const payload = unwrapAdminResponse(res)
        const list = Array.isArray(payload?.brands) ? payload.brands : []
        this.brands = list.filter(brand => brand && brand.id && brand.is_active !== false)
        const selectedStillValid = this.brands.some(brand => String(brand.id) === String(this.editForm.brandId))
        if (!selectedStillValid && this.brands.length === 1) {
          this.editForm.brandId = String(this.brands[0].id)
        } else if (!selectedStillValid) {
          this.editForm.brandId = ''
        }
      } catch (e) {
        this.brands = []
      }
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back()
        return
      }
      this.$router.push({ name: 'ads-video-management' })
    },
    openEditModal() {
      if (!this.ad) return
      const start = this.parseScheduleParts(
        this.ad.publish_at_raw || '',
        this.ad.publish_at || '',
        this.ad.publish_time || this.ad.scheduled_at?.time || '',
      )
      const end = this.parseScheduleParts(
        this.ad.expires_at_raw || '',
        this.ad.expires_at || '',
        this.ad.expires_time || '',
      )
      const fallbackEnd = new Date()
      fallbackEnd.setDate(fallbackEnd.getDate() + 7)
      this.editForm = {
        title: this.ad.title || '',
        description: this.ad.description || '',
        company: String(this.ad.company.id || ''),
        brandId: this.ad.brand_id ? String(this.ad.brand_id) : (this.ad.brand?.id ? String(this.ad.brand.id) : ''),
        categoryId: this.ad.category_id || null,
        secondaryCategoryId: this.ad.secondary_category_id || null,
        minAge: Number(this.ad.min_age || 0),
        ctaLabel: this.ad.cta_label || '',
        ctaLink: this.ad.cta_link || '',
        startDate: start.date || new Date().toISOString().slice(0, 10),
        startTime: start.time || '10:00',
        endDate: end.date || fallbackEnd.toISOString().slice(0, 10),
        endTime: end.time || '22:00',
      }
      this.existingImage = this.ad.image || ''
      this.loadBrands(this.editForm.company)
      this.selectedEditFile = null
      this.revokeEditPreview()
      this.scheduleError = ''
      this.editModalOpen = true
    },
    onEditPrimaryCategoryChange() {
      this.editForm.secondaryCategoryId = null
    },
    revokeEditPreview() {
      if (this.editFilePreviewUrl) {
        URL.revokeObjectURL(this.editFilePreviewUrl)
        this.editFilePreviewUrl = ''
      }
    },
    onEditFilePick(event) {
      const [file] = event.target.files || []
      if (event.target) event.target.value = ''
      if (!file || (!isAllowedAdImageFile(file) && !isAllowedAdVideoFile(file))) {
        notifyError(this, `${AD_IMAGE_FORMAT_ERROR} ${AD_VIDEO_FORMAT_ERROR}`)
        return
      }
      this.revokeEditPreview()
      this.selectedEditFile = file
      this.editFilePreviewUrl = URL.createObjectURL(file)
    },
    resetEditForm() {
      this.selectedEditFile = null
      this.revokeEditPreview()
      this.scheduleError = ''
      this.editSaving = false
    },
    validateSchedule() {
      this.scheduleError = ''
      const {
        startDate, endDate, startTime, endTime,
      } = this.editForm
      if (!startDate || !endDate || !startTime || !endTime) {
        this.scheduleError = 'Publish and expiry date/time are required.'
        return false
      }
      const start = new Date(`${startDate}T${startTime}:00`)
      const end = new Date(`${endDate}T${endTime}:00`)
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        this.scheduleError = 'Invalid publish or expiry date/time.'
        return false
      }
      if (end <= start) {
        this.scheduleError = 'Expiry date/time must be after publish date/time.'
        return false
      }
      return true
    },
    async saveEdit() {
      const valid = await this.$refs.editFormObserver.validate()
      if (!valid || !this.validateSchedule()) return
      if (!this.editForm.company) {
        notifyError(this, 'Please select an advertiser.')
        return
      }

      this.editSaving = true
      let imageUrl = ''
      try {
        if (this.selectedEditFile) {
          const uploadForm = new FormData()
          uploadForm.append('file', this.selectedEditFile)
          const uploadRes = await adminPanelApi.uploadMedia(uploadForm)
          imageUrl = unwrapAdminResponse(uploadRes)?.url || ''
        }

        const form = new FormData()
        form.append('title', this.editForm.title)
        form.append('description', this.editForm.description)
        form.append('cta_label', this.editForm.ctaLabel)
        form.append('cta_link', this.editForm.ctaLink)
        form.append('advertiser_id', String(this.editForm.company))
        form.append('brand_id', String(this.editForm.brandId || ''))
        form.append('category_id', String(this.editForm.categoryId || ''))
        if (this.editForm.secondaryCategoryId) {
          form.append('secondary_category_id', String(this.editForm.secondaryCategoryId))
        } else {
          form.append('secondary_category_id', '')
        }
        form.append('min_age', String(this.editForm.minAge ?? 0))
        form.append('start_date', this.editForm.startDate)
        form.append('start_time', this.editForm.startTime)
        form.append('end_date', this.editForm.endDate)
        form.append('end_time', this.editForm.endTime)
        if (imageUrl) {
          form.append('image_url', imageUrl)
        } else if (this.selectedEditFile) {
          form.append('image', this.selectedEditFile)
        }

        await adminPanelApi.updateAd(this.ad.id, form)
        notifySuccess(this, 'Ad updated successfully.')
        this.editModalOpen = false
        await this.fetchAd()
      } catch (e) {
        notifyError(this, extractApiErrorMessage(e, 'Unable to update ad.'))
      } finally {
        this.editSaving = false
      }
    },
    approveAd() {
      if (this.actionInFlight) return
      confirmAction(
        this,
        { title: 'Approve ad?', text: `Approve "${this.ad.title}"?`, confirmButtonText: 'Approve' },
        async () => {
          this.actionInFlight = true
          try {
            await adminPanelApi.approveAd(this.ad.id)
            notifySuccess(this, 'Ad approved successfully.')
            await this.fetchAd()
          } catch (e) {
            notifyError(this, extractApiErrorMessage(e, 'Unable to approve ad.'))
          } finally {
            this.actionInFlight = false
          }
        },
      )
    },
    async rejectAd() {
      if (this.actionInFlight) return
      const reason = await promptRejectReason(this, {
        title: 'Reject ad',
        text: `Provide a reason for rejecting "${this.ad.title}".`,
      })
      if (!reason) return

      this.actionInFlight = true
      try {
        await adminPanelApi.rejectAd({ id: this.ad.id, reason })
        notifySuccess(this, 'Ad rejected successfully.')
        await this.fetchAd()
      } catch (e) {
        notifyError(this, extractApiErrorMessage(e, 'Unable to reject ad.'))
      } finally {
        this.actionInFlight = false
      }
    },
    deleteAd() {
      if (this.actionInFlight) return
      confirmAction(
        this,
        { title: 'Delete ad?', text: 'This ad will be removed permanently.', confirmButtonText: 'Delete' },
        async () => {
          this.actionInFlight = true
          try {
            await adminPanelApi.deleteAd(this.ad.id)
            notifySuccess(this, 'Ad deleted successfully.')
            this.$router.push({ name: 'ads-video-management' })
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to delete ad.')
          } finally {
            this.actionInFlight = false
          }
        },
      )
    },
  },
}
</script>

<style scoped lang="scss">
.detail-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.detail-toolbar__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.media-card {
  overflow: hidden;
  border: 1px solid #ebe9f1;
}

.media-wrap {
  background: #111;
}

.ad-image,
.ad-video {
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  display: block;
}

.media-thumbs {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  border-top: 1px solid #ebe9f1;
}

.thumb-btn {
  border: 1px solid #d8d6de;
  border-radius: 6px;
  padding: 0;
  background: #fff;
  width: 72px;
  height: 52px;
  overflow: hidden;
}

.thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-video {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 0.7rem;
  color: #6e6b7b;
}

.detail-card {
  border: 1px solid #ebe9f1;
}

.ad-title {
  font-size: 1.35rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.ad-description {
  color: #6e6b7b;
  white-space: pre-wrap;
}

.detail-list {
  margin: 1rem 0 0;
}

.detail-list dt {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6e6b7b;
  margin-top: 0.75rem;
}

.detail-list dd {
  margin-bottom: 0;
  color: #2f2b3d;
  word-break: break-word;
  font-weight: 500;
}

.stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #f0eff5;
  color: #6e6b7b;
  font-size: 0.9rem;
}

.edit-section-title {
  margin: 0.5rem 0 0.75rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #5e5873;
}

.edit-media-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.edit-preview {
  width: 96px;
  height: 72px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #ebe9f1;
}
</style>
